<template>
  <div class="form-group">
    <label
      v-if="label !== ''"
      class="text-gray-600"
    > {{ label }} </label>
    <input
      v-if="path.includes('siret')"
      v-siret="{
        getValue: customValue,
        setValue: setCustomModel,
      }"
      type="text"
      class="form-control"
      :disabled="disabled"
      :class="customValidation"
      :placeholder="placeholder"
    >
    <template v-else-if="typeOf === 'number'">
      <div class="input-group">
        <input
          v-number="{
            getValue: customValue,
            setValue: setCustomModel,
          }"
          type="tel"
          class="form-control"
          :disabled="disabled"
          :class="customValidation"
          :placeholder="placeholder"
        >
      </div>
    </template>
    <template v-else-if="path.includes('phone')">
      <div class="input-group">
        <input
          v-phone="{
            getValue: customValue,
            setValue: setCustomModel,
            options: {
              setWithMask: true,
            }
          }"
          type="tel"
          class="form-control"
          :disabled="disabled"
          :class="customTelValidation"
          :placeholder="placeholder"
        >
      </div>
    </template>
    <template v-else-if="typeOf === 'birthdate'">
      <div class="input-group">
        <input
          v-birthdate="{
            getValue: format(customValue),
            setValue: setCustomDate,
          }"
          type="tel"
          class="form-control"
          :disabled="disabled"
          :class="customValidation"
          placeholder="__/__/____"
        >
      </div>
    </template>
    <template v-else-if="typeOf === 'echeance'">
      <div class="input-group">
        <input
          v-echeance="{
            getValue: customValue,
            setValue: setCustomModel,
            options: {
              setWithMask: true,
            }
          }"
          type="tel"
          class="form-control"
          :disabled="disabled"
          :class="customValidation"
          placeholder="JJ/MM"
        >
      </div>
    </template>
    <template v-else-if="typeOf === 'currency'">
      <div class="input-group">
        <input
          v-number="{
            getValue: customValue,
            setValue: setCustomModel,
          }"
          type="tel"
          class="form-control"
          :disabled="disabled"
          :class="customValidation"
          :placeholder="placeholder"
        >
        <div class="input-group-append">
          <div class="input-group-custom-content">
            <font-awesome-icon
              icon="euro-sign"
            />
          </div>
        </div>
      </div>
    </template>
    <template v-else-if="typeOf === 'area'">
      <div class="input-group">
        <textarea
          :value="customValue"
          class="form-control"
          :disabled="disabled"
          :class="customValidation"
          :placeholder="placeholder"
          rows="3"
          @input="setCustomModel($event.target.value)"
        />
      </div>
    </template>
    <input
      v-else-if="path.includes('orias')"
      v-orias="{
        getValue: customValue,
        setValue: setCustomModel,
      }"
      type="text"
      class="form-control"
      :disabled="disabled"
      :class="customValidation"
      :placeholder="placeholder"
    >
    <input
      v-else-if="path.includes('iban')"
      v-iban="{
        getValue: customValue,
        setValue: setCustomModel,
      }"
      type="text"
      class="form-control"
      :disabled="disabled"
      :class="customValidation"
      :placeholder="placeholder"
    >
    <div
      v-else-if="path.includes('password')"
      class="input-group"
    >
      <input
        :value="customValue"
        :type="selected ? 'text': 'password'"
        class="form-control"
        :disabled="disabled"
        :class="customValidation"
        :placeholder="placeholder"
        @input="setCustomModel($event.target.value)"
      >
      <div class="input-group-append">
        <div
          class="input-group-custom-content cursor-pointer"
          @click="selected = !selected"
        >
          <font-awesome-icon
            :icon="selected ? icon[0] : icon[1]"
            :class="selected ? 'text-blue-500' : ''"
            style="width:19px; height: 19px"
          />
        </div>
      </div>
    </div>
    <div
      v-else
      class="input-group"
    >
      <input
        :value="customValue"
        class="form-control"
        :type="typeOf"
        :disabled="disabled"
        :class="customValidation"
        :style="inputStyleBuilder"
        :placeholder="placeholder"
        @input="setCustomModel($event.target.value)"
        @focus="(event) => $emit('focus', event)"
        @blur="(event) => $emit('blur', event)"
        @keyup.enter="$event.target.blur()"
      >
      <div
        v-if="icon !== '' && icon !== 'none'"
        class="input-group-append"
      >
        <span
          v-if="typeof icon === 'string'"
          class="input-group-custom-content"
        >
          {{ icon === 'none' ? '' : icon }}
        </span>
        <span
          v-else-if="icon.length === 1"
          class="input-group-custom-content"
        >
          <font-awesome-icon
            :icon="icon[0]"
            style="width:19px; height: 19px"
          />
        </span>
      </div>
    </div>

    <transition
      tag="div"
      @enter="tf_slideDown"
      @leave="tf_slideUp"
    >
      <div
        v-if="isErrorMessage"
        class="invalid-feedback"
      >
        {{ errorMessage }}
      </div>
    </transition>
  </div>
</template>
<script>
import { get, isNil, debounce } from 'lodash-es';
import { transform, input, ui } from '@/mixins';

export default {
  name: 'DefaultInput',
  mixins: [transform, input, ui],
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    validation: {
      type: Function,
      default: (value) => value !== '' || value !== undefined,
    },
    inputCallback: {
      type: Function,
      default: () => {},
    },
    errorMessage: {
      type: String,
      required: false,
      default: 'Veuillez compléter le champ',
    },
    typeOf: {
      type: String,
      required: false,
      default: 'text',
    },
    icon: {
      type: [String, Array],
      required: false,
      default: () => '',
    },
  },
  data: () => ({
    selected: false,
  }),
  computed: {
    customValidation() {
      if (this.isEmpty(this.customValue)) return '';
      return this.validation(this.customValue) ? '' : 'is-invalid';
    },
    customTelValidation() {
      if (this.isEmpty(this.customValue)) return '';
      return /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/.test(this.customValue)
        ? '' : 'is-invalid';
    },
    isErrorMessage() {
      if (this.customModel === '') return false;
      return !this.validation(this.customModel);
    },
    inputStyleBuilder() {
      const height = this.path === 'searchTerm' ? 'min-height: 60px;' : '';
      return `${height}`;
    },
  },
  beforeMount() {
    this.customValue = get(this.$store.state[this.actionModule], this.path);
  },
  beforeUnmount() {
    if (this.path === 'searchTerm' && this.$store.getters['ui/focusScreen']) {
      this.ui_focusScreen(false);
    }
  },
  methods: {
    isEmpty(value) {
      return value === '' || isNil(value) || Number.isNaN(value);
    },
    format(date) {
      try {
        let day = date.getDate();
        let month = date.getMonth() + 1;
        const year = date.getFullYear();

        if (day < 10) day = `0${day}`;
        if (month < 10) month = `0${month}`;
        return [day, month, year].join('/');
      } catch {
        return '';
      }
    },
    setCustomDate(value) {
      if (this.path === 'searchTerm' && !this.$store.getters['ui/focusScreen']) {
        this.ui_focusScreen(true);
      }
      this.customValue = value;
      const yearMonthDay = value.split('/').map(Number);
      const date = new Date(yearMonthDay[2], yearMonthDay[1] - 1, yearMonthDay[0]);
      this.debouncer.cancel();
      const setValue = () => {
        this.$store.dispatch(this.actionStore, { path: this.path, value: date });
        this.inputCallback();
      };
      this.debouncer = debounce(setValue, this.debounceTimer);
      this.debouncer();
    },
    setCustomModel(value) {
      if (this.path === 'searchTerm' && !this.$store.getters['ui/focusScreen']) {
        this.ui_focusScreen(true);
      }
      this.customValue = value;
      this.debouncer.cancel();
      const setValue = () => {
        this.$store.dispatch(this.actionStore, { path: this.path, value });
        this.inputCallback();
      };
      this.debouncer = debounce(setValue, this.debounceTimer);
      this.debouncer();
    },
  },
};
</script>

<style scoped>
/* Input component styles */
</style>
